/**
 * Motor de decisión de "unirse" (documento 05a, §6; documento 05b): el archivo único
 * compartido por navegador y servidor que exige RN-1 del documento 05a — "el navegador
 * enseña, el servidor permite, con la misma fuente, así que nunca divergen" (RN-2). Funciones
 * puras, sin estado ni red, sin importar Nest, Prisma ni React (ADR-0017): el llamador ya trae
 * las filas de "experienceJoin" que hagan falta, y ninguna función de aquí consulta la base.
 *
 * Ninguna función llama a `Date.now()` ni construye `new Date()` sin argumentos: la hora
 * entra siempre como parámetro (`ahora`), mismo criterio que `rules/invitationTips.ts` — así
 * el resultado es reproducible con valores fijos en un test, sin depender del reloj del
 * sistema.
 *
 * Ningún texto para humanos: solo claves i18n como motivo (documento 05a §6.7, "la API no
 * manda texto humano"). Este fichero no importa de `contracts/` (ADR-0017: `rules/` es
 * terreno independiente): las uniones de literales que ya declara `contracts/experienceJoins`
 * se reescriben aquí a mano, con nombres de tipo DISTINTOS a los del contrato para que el
 * barrel `src/index.ts` (que reexporta `contracts/` y `rules/` con `export *`) no choque por
 * un nombre duplicado — mismo criterio que `TierCreacionExperiencia`/`EtiquetaAccesoExperiencia`
 * en `rules/experiences.ts` frente a `UserTier`/`ExperienceAccessLabel` de `contracts/`.
 */

import {
	aplicaReservaNovatos,
	type EtiquetaAccesoExperiencia,
	plazasReservadas,
	reservaEfectivaPct,
} from './experiences';

// -----------------------------------------------------------------------------
// Constantes de negocio (documento 05a, varias secciones citadas en cada una).
// -----------------------------------------------------------------------------

/** Margen para que cancelar cuente como "avisar" en vez de "a última hora" (§5.1, D-29). */
export const MARGEN_AVISO_HORAS = 24;

/**
 * Lo que paga el grumete por un plan sin precio propio, a partir de su segunda experiencia
 * (§6.5). Puesta a **0** por decisión cerrada del cliente (respuestas 2026-09-17, P-07): «La
 * cuota del segundo plan del grumete (10 €) se queda en 0 hasta que exista la pasarela. Esa sí
 * la cobraríamos nosotros, y regalar plazas de pago sin enterarnos no». Vuelve a **1000** (10 €)
 * con la entrega 07 (membresía y pagos), cuando exista la pasarela que de verdad la cobre — no
 * antes: cobrar sin poder procesar el pago sería justo el error que P-07 señala.
 */
export const CUOTA_PLAN_GRUMETE_CENTS = 0;

/** Horas antes del inicio en las que la reserva de novatos se libera (documento 03c, P-10.4). */
export const LIBERACION_RESERVA_HORAS = 72;

/** Ausencias sin avisar que activan el bloqueo temporal (§6.6). */
export const AUSENCIAS_PARA_BLOQUEO = 2;

/** Duración del bloqueo por ausencias, en días (§6.6). */
export const DIAS_DE_BLOQUEO = 7;

/** Los cuatro umbrales del ratio de asistencia, en porcentaje (§6.6). */
export const UMBRAL_ASISTENCIA_ALTA_PCT = 80;
export const UMBRAL_ASISTENCIA_MEDIA_PCT = 60;
export const UMBRAL_ASISTENCIA_BAJA_PCT = 40;
export const UMBRAL_ASISTENCIA_CRITICA_PCT = 20;

/** Compromisos mínimos para que el ratio bajo dispare una consecuencia real (§6.6). */
export const MINIMO_COMPROMISOS_ADVERTENCIA_FINAL = 3;
export const MINIMO_COMPROMISOS_CUENTA_BLOQUEADA = 5;

// Building blocks de conversión de tiempo: Biome (`noMagicNumbers`) exige nombrar cualquier
// número que no sea 0 o 1 — mismo patrón que `MILISEGUNDOS_POR_SEGUNDO` en
// `core/auth/sesion.middleware.ts` y `experienceDrafts.service.ts`.
const SEGUNDOS_POR_MINUTO = 60;
const MINUTOS_POR_HORA = 60;
const HORAS_POR_DIA = 24;
const MILISEGUNDOS_POR_SEGUNDO = 1000;

/** Milisegundos que dura una hora — el factor de conversión de horas a milisegundos. */
const MS_POR_HORA = MINUTOS_POR_HORA * SEGUNDOS_POR_MINUTO * MILISEGUNDOS_POR_SEGUNDO;

/** Milisegundos que dura un día — el factor de conversión de `DIAS_DE_BLOQUEO` a milisegundos. */
const MS_POR_DIA = HORAS_POR_DIA * MS_POR_HORA;

/** El 100 % del ratio de asistencia — ni valor por defecto arbitrario ni número mágico suelto. */
const PORCENTAJE_COMPLETO = 100;

// -----------------------------------------------------------------------------
// Tipos redeclarados a mano (ADR-0017: `rules/` no importa de `contracts/`).
// -----------------------------------------------------------------------------

/** Los siete estados de una participación (documento 05a §5.2, `contracts/experienceJoins` `JoinStatus`). */
export type EstadoParticipacion =
	| 'REQUESTED'
	| 'WAITLISTED'
	| 'CONFIRMED'
	| 'ATTENDED'
	| 'NO_SHOW'
	| 'CANCELLED'
	| 'REJECTED';

/** Una fila de "experienceJoin" reducida a lo que este motor necesita para decidir un bloqueo. */
export interface ParticipacionParaBloqueo {
	status: EstadoParticipacion;
	/**
	 * Cuándo se marcó este estado. La migración no guarda una fecha propia para "NO_SHOW"
	 * (documento 05a §5.1: solo `cancelledAt` es una fecha cruda dedicada); se usa la marca de
	 * actualización de la fila, que el disparador `set_updated_at` mueve exactamente cuando el
	 * organizador cierra la experiencia y decide quién faltó (documento 05a §9).
	 */
	updatedAt: Date;
}

// -----------------------------------------------------------------------------
// esNovato — el criterio es un hito, no un rol (documento 05a §6.3).
// -----------------------------------------------------------------------------

/**
 * Si esta persona todavía no ha vivido ninguna experiencia: ninguna de sus participaciones
 * está en `ATTENDED` (documento 05a §6.3 — "se calcula con una sola consulta: ¿tiene alguna
 * participación en estado asistió?"). Da igual el rol: pendiente, grumete que se quedó fuera
 * tres veces, o alguien invitado por un miembro — si aún no ha vivido ninguna, es novato.
 */
export const esNovato = (joins: readonly EstadoParticipacion[]): boolean => !joins.includes('ATTENDED');

// -----------------------------------------------------------------------------
// La reserva de plazas por hito, con liberación a las 72 horas (documento 05a §6.3; documento
// 03c, P-10.4). Reutiliza `aplicaReservaNovatos`/`reservaEfectivaPct`/`plazasReservadas`, ya
// construidas en `rules/experiences.ts` — no se reescriben aquí.
// -----------------------------------------------------------------------------

export interface ContextoPlazasGuardadas {
	accessLabel: EtiquetaAccesoExperiencia;
	/** El instante contra el que se mide la cuenta atrás de liberación. */
	ahora: Date;
	capacity: number | null;
	newcomerReservePct: number;
	/** `null` nunca libera la reserva: sin fecha no hay cuenta atrás que iniciar (mismo criterio que CL-7, aplicado aquí a la liberación en vez de a la cancelación). */
	startDate: Date | null;
}

/**
 * Plazas efectivamente guardadas para quien todavía no ha vivido ninguna experiencia, ya
 * aplicadas las dos condiciones que las anulan: `0` si `capacity` es nulo (CL-8 del documento
 * 05a: sin aforo no hay reserva, porque no hay nada que reservar) y `0` si faltan menos de
 * `LIBERACION_RESERVA_HORAS` para `startDate` (documento 03c, P-10.4: la promesa a la
 * comunidad es proteger dos tercios, no dejar plazas vacías el día del plan). También `0` si
 * la etiqueta de acceso no aplica reserva (`aplicaReservaNovatos`, documento 03c: `OFFICIAL`,
 * `COLLABORATOR` y `BRAND`, no solo `OFFICIAL` como decía el documento 05a §6.3 — D-5 cerrada
 * por el documento 03c, posterior).
 */
export const plazasGuardadasEfectivas = (contexto: ContextoPlazasGuardadas): number => {
	if (!aplicaReservaNovatos(contexto.accessLabel)) {
		return 0;
	}
	if (contexto.capacity === null) {
		return 0;
	}
	if (contexto.startDate !== null) {
		const horasHastaInicio = (contexto.startDate.getTime() - contexto.ahora.getTime()) / MS_POR_HORA;
		if (horasHastaInicio < LIBERACION_RESERVA_HORAS) {
			return 0;
		}
	}
	return plazasReservadas(contexto.capacity, reservaEfectivaPct(contexto.newcomerReservePct));
};

// -----------------------------------------------------------------------------
// hayPlazaPara — la tabla de escenarios del §6.3.
// -----------------------------------------------------------------------------

export interface ContextoHayPlaza {
	capacity: number | null;
	/** Confirmados totales (cualquier rol). */
	confirmados: number;
	/** Si quien pregunta es, a estos efectos, novato (`esNovato`, YA CALCULADO). */
	esNovato: boolean;
	/** De los confirmados totales, cuántos son novatos — ocupan la reserva, no el resto del aforo. */
	novatosConfirmados: number;
	/** `plazasGuardadasEfectivas`, YA CALCULADO. */
	plazasGuardadas: number;
}

/**
 * Si hay plaza para quien pregunta (documento 05a §6.3, tabla de escenarios con 15 plazas y
 * reserva de 5).
 *
 * Error de conjuntos corregido (hallazgo B4/H-09, auditoría funcional 2026-09-10): la versión
 * anterior restaba `novatosConfirmados` completo del aforo no reservado
 * (`confirmados - novatosConfirmados`), asumiendo que TODO novato confirmado ocupa solo la
 * reserva. Pero si los novatos confirmados superan las plazas guardadas, el exceso ya no cabe
 * en la reserva y desborda al aforo general — ese exceso SÍ ocupa aforo general, igual que
 * cualquier no-novato. Restar el total de novatos entonces libera hueco de más y sobreestima
 * la plaza disponible.
 *
 * Modelo correcto: primero, si el aforo total ya está lleno (`confirmados >= capacity`), no
 * hay plaza para nadie, sea cual sea su rol. Si no está lleno, un novato compite por el aforo
 * entero (`confirmados < capacity`, ya garantizado por la guarda anterior), porque puede ocupar
 * tanto la reserva como el resto. Quien no es novato solo compite por el aforo NO reservado
 * (`capacity - plazasGuardadas`); lo que ya ocupa ese aforo no reservado son los confirmados
 * que no son novatos, MÁS el exceso de novatos que no cupo en la reserva
 * (`confirmados - Math.min(novatosConfirmados, plazasGuardadas)`) — así, si los cinco novatos
 * que ya entraron cubrieron toda la reserva, el resto de plazas vuelve a ser de cualquiera
 * (documento 05a §6.3, tercer escenario: "la reserva ya se cubrió"), y si además hubiera un
 * sexto novato confirmado, ese sexto cuenta como aforo general igual que un no-novato.
 * `capacity` nulo significa aforo ilimitado: siempre hay sitio (CL-9).
 */
export const hayPlazaPara = (contexto: ContextoHayPlaza): boolean => {
	if (contexto.capacity === null) {
		return true;
	}
	if (contexto.confirmados >= contexto.capacity) {
		return false;
	}
	if (contexto.esNovato) {
		return true;
	}
	const poolGeneral = contexto.capacity - contexto.plazasGuardadas;
	const ocupadoGeneral = contexto.confirmados - Math.min(contexto.novatosConfirmados, contexto.plazasGuardadas);
	return ocupadoGeneral < poolGeneral;
};

// -----------------------------------------------------------------------------
// evaluarUnirse — el árbol de decisión del §6.2.
// -----------------------------------------------------------------------------

/**
 * Los cuatro desenlaces de intentar unirse (documento 05a §6.2, §6.7). `blocked` es una
 * DESVIACIÓN DELIBERADA sobre el §6.2 del PDF, que solo declara tres desenlaces: el documento
 * 05b §10.1 exige una pantalla propia de "bloqueado por ausencias" y CL-9 del documento 05a
 * obliga a comprobar el bloqueo en el servidor, no solo en la pantalla — ninguno de los otros
 * tres desenlaces encaja (no es un `redirect`, porque no hay a dónde redirigir: el bloqueo es
 * temporal, no un muro).
 */
export type ResultadoUnirseOutcome = 'join' | 'waitlist' | 'redirect' | 'blocked';

/** Los cuatro destinos posibles de una redirección (documento 05a §6.7; documento 05b §10.2). */
export type ResultadoUnirseRedirectTo = 'createAccount' | 'membershipGate' | 'invitationRequired' | 'recommendations';

/** El motivo en código de la decisión (documento 05a §6.7: "la API no manda texto humano"). */
export type ResultadoUnirseReason =
	| 'planPrivadoSinInvitacion'
	| 'sinCuenta'
	| 'soloParaMiembros'
	| 'hayPlaza'
	| 'plazasGuardadasParaNuevos'
	| 'aforoCompleto'
	| 'bloqueadoPorAusencias'
	| 'yaTienesPlaza';

export interface ResultadoEvaluarUnirse {
	blockedUntil?: string;
	outcome: ResultadoUnirseOutcome;
	reason: ResultadoUnirseReason;
	redirectTo?: ResultadoUnirseRedirectTo;
}

export interface ContextoEvaluarUnirse {
	accessLabel: EtiquetaAccesoExperiencia;
	/** `bloqueadoPorAusencias(joins, ahora).hasta`, como ISO, si `bloqueoActivo` es cierto. */
	blockedUntil?: string;
	/** Salida de `bloqueadoPorAusencias(joins, ahora).bloqueado`, YA CALCULADA por quien llama. */
	bloqueoActivo: boolean;
	/** `hayPlazaPara(...)`, YA CALCULADA por quien llama. */
	hayPlaza: boolean;
	/**
	 * Si, con `capacity` no nulo, quedan sitios libres en total (`confirmados < capacity`),
	 * aunque `hayPlaza` sea falso porque esos sitios son de la reserva de novatos. Solo importa
	 * cuando `hayPlaza` es falso — decide entre los dos motivos de lista de espera (RN-13 del
	 * documento 05b: nunca "completo" si en realidad quedan sitios guardados).
	 */
	hayPlazaLibreEnTotal: boolean;
	haySesion: boolean;
	/**
	 * Si el visor puede entrar a un plan de solo miembros — la misma pregunta que resuelve
	 * `ACCESS_MATRIX.joinMemberPlans` (`rules/accessMatrix.ts`): cierto para miembro o
	 * colaborador, falso para grumete o pendiente (CA-05a.4: "un miembro o colaborador pasa").
	 */
	puedeAccederMembersOnly: boolean;
	tieneInvitacionValida: boolean;
}

/**
 * El árbol de decisión de unirse (documento 05a §6.2): cuatro comprobaciones en este orden,
 * la primera que salta manda (RN-3) — por eso alguien sin cuenta ante un plan privado ve lo
 * del enlace, no lo de la cuenta. Antes de las cuatro, el bloqueo por ausencias corta primero
 * (CL-9): ni siquiera se llega a mirar la etiqueta de acceso.
 *
 * (1) plan privado sin invitación → `redirect` a `invitationRequired`.
 * (2) sin cuenta → `redirect` a `createAccount` (RN-4 del documento 05b: es adquisición, no un muro).
 * (3) `MEMBERS_ONLY` y no puede acceder → `redirect` a `membershipGate`.
 * (4) ¿hay plaza? → `join` o `waitlist`, con el motivo correcto en cada caso.
 *
 * **No existe ningún corte al grumete** (RN-4 del documento 05a): el grumete entra a todas las
 * oficiales, así que ninguna comprobación de este árbol lo bloquea por su rol — el control es
 * de caja (`costeDeUnirse`), no de puerta.
 *
 * El desenlace `yaTienesPlaza` (CA-05a.17: nadie se une dos veces a lo mismo) NO forma parte de
 * este árbol: es responsabilidad de quien llama, que ya conoce la participación existente del
 * visitante antes de invocar `evaluarUnirse`, y decide ahí mismo devolver ese desenlace sin
 * recorrer el árbol — el documento 05a §6.2 tampoco lo incluye entre las cuatro comprobaciones.
 */
export const evaluarUnirse = (contexto: ContextoEvaluarUnirse): ResultadoEvaluarUnirse => {
	if (contexto.bloqueoActivo) {
		return contexto.blockedUntil === undefined
			? { outcome: 'blocked', reason: 'bloqueadoPorAusencias' }
			: { blockedUntil: contexto.blockedUntil, outcome: 'blocked', reason: 'bloqueadoPorAusencias' };
	}
	if (contexto.accessLabel === 'PRIVATE' && !contexto.tieneInvitacionValida) {
		return { outcome: 'redirect', reason: 'planPrivadoSinInvitacion', redirectTo: 'invitationRequired' };
	}
	if (!contexto.haySesion) {
		return { outcome: 'redirect', reason: 'sinCuenta', redirectTo: 'createAccount' };
	}
	if (contexto.accessLabel === 'MEMBERS_ONLY' && !contexto.puedeAccederMembersOnly) {
		return { outcome: 'redirect', reason: 'soloParaMiembros', redirectTo: 'membershipGate' };
	}
	if (contexto.hayPlaza) {
		return { outcome: 'join', reason: 'hayPlaza' };
	}
	return contexto.hayPlazaLibreEnTotal
		? { outcome: 'waitlist', reason: 'plazasGuardadasParaNuevos' }
		: { outcome: 'waitlist', reason: 'aforoCompleto' };
};

// -----------------------------------------------------------------------------
// loIntentoYNoPudo — D-22, criterio confirmado por el cliente (P-21), con su matiz ya aplicado.
// -----------------------------------------------------------------------------

/**
 * Quién canceló una participación — columna `cancelledBy` de `experienceJoin`, migración
 * `quienCancelaUnaParticipacion` (P-21). Redeclarado a mano del enumerado `JoinCancelledBy` de
 * Prisma, mismo criterio que `EstadoParticipacion` frente a `JoinStatus` (ADR-0017: `rules/` no
 * importa del cliente generado). Los tres orígenes reales que escribe el código hoy: la propia
 * persona avisando de que no va (`PARTICIPANT`), el plan entero cancelándose en cascada
 * (`PLAN`), y la ejecución de una baja de cuenta, que solo dispara ADMIN (`ACCOUNT_DELETION`).
 */
export type QuienCancelo = 'PARTICIPANT' | 'PLAN' | 'ACCOUNT_DELETION';

/**
 * Una fila de `experienceJoin` con su estado y, si está `CANCELLED`, quién la canceló —lo que
 * necesita `loIntentoYNoPudo` desde que el criterio distingue por quién. `cancelledBy` ausente o
 * `null` significa "no se sabe" (columna nullable a propósito: el hueco histórico de antes de la
 * migración — ver el porqué de tratarlo como "cuenta" en el JSDoc de `loIntentoYNoPudo`).
 */
export interface ParticipacionConCancelacion {
	cancelledBy?: QuienCancelo | null;
	status: EstadoParticipacion;
}

/**
 * Lo que acepta `loIntentoYNoPudo` por cada participación del historial: un estado a secas, o la
 * fila completa con quién canceló cuando se conoce. Compatibilidad hacia atrás deliberada: un
 * `EstadoParticipacion[]` sigue siendo un `ParticipacionParaLoIntento[]` válido sin cambiar una
 * sola línea de quien ya llama a esta función solo con estados (p. ej.
 * `ExperienceModerationService`, fuera del alcance de esta tarea, que hoy solo consulta
 * `status`) — cada elemento string encaja en la mitad izquierda de la unión.
 */
export type ParticipacionParaLoIntento = EstadoParticipacion | ParticipacionConCancelacion;

/**
 * El criterio computable de "lo intentó y no pudo" (documento 05a §15, D-22): al menos una
 * participación `REJECTED`, `CANCELLED` o `WAITLISTED`, y ninguna `ATTENDED`. El criterio base
 * quedó **confirmado por el cliente el 17/09/2026 (P-21)**, y esta función ya aplica también el
 * matiz de esa misma respuesta: «si la canceló ella misma, no cuenta [...] "lo intentó y no
 * pudo" es para quien se quedó fuera, no para quien se lo pensó mejor [...] si la cancelación
 * fue del plan o la decidió quien organiza, sí». Por eso una fila `CANCELLED` con
 * `cancelledBy: 'PARTICIPANT'` NO cuenta, y con `'PLAN'` o `'ACCOUNT_DELETION'` cuenta igual que
 * `REJECTED`/`WAITLISTED`.
 *
 * **El hueco histórico** (`cancelledBy` ausente o `null` — toda cancelación anterior a la
 * migración `quienCancelaUnaParticipacion`, que no se puede inventar): CUENTA, exactamente igual
 * que antes de aplicar este matiz. Dos razones. La primera, de cómo razonar la laguna: la regla
 * BASE, ya confirmada y fuera de discusión, es que una `CANCELLED` cuenta; la corrección de P-21
 * es una EXCEPCIÓN sobre esa base, que solo se activa cuando hay constancia de que la decidió la
 * propia persona — sin ese dato, se aplica la regla base, no la excepción (la carga de la prueba
 * es de la excepción). Tratar el hueco como "no cuenta" equivaldría a asumir que sí fue ella sin
 * ningún hecho que lo respalde — el mismo "no se puede inventar" que ya impide rellenar la
 * columna a mano, aplicado aquí al comportamiento en vez de al dato. La segunda, de efecto
 * práctico: así el historial de nadie empeora en silencio el día que se despliega esta
 * migración — pasar de "cuenta" a "no cuenta" para una cancelación que ya había ocurrido, sin
 * ningún hecho nuevo detrás, bajaría a alguien de escalón en la lista de espera por una
 * migración, no por nada que haya hecho. Es, de los dos errores posibles, el de contar de más
 * (alguien que se lo pensó mejor, de antes de esta migración, conserva el escalón que ya tenía)
 * en vez del de contar de menos (alguien que de verdad se quedó fuera lo pierde) — y además un
 * error que no crece: solo afecta a cancelaciones anteriores a hoy, nunca a una nueva.
 *
 * Aislada en su propia función a propósito, igual que antes — el matiz vive local a esta
 * función, sin tocar `pesoListaEspera` ni ningún llamador.
 */
export const loIntentoYNoPudo = (joins: readonly ParticipacionParaLoIntento[]): boolean => {
	const normalizadas = joins.map(fila => (typeof fila === 'string' ? { status: fila } : fila));
	if (normalizadas.some(fila => fila.status === 'ATTENDED')) {
		return false;
	}
	return normalizadas.some(
		fila =>
			fila.status === 'REJECTED'
			|| fila.status === 'WAITLISTED'
			|| (fila.status === 'CANCELLED' && fila.cancelledBy !== 'PARTICIPANT'),
	);
};

// -----------------------------------------------------------------------------
// El orden de la lista de espera (documento 05a §6.4).
// -----------------------------------------------------------------------------

/** Los cuatro niveles del orden de la lista de espera (documento 05a §6.4), de más a menos prioridad. */
export type RolListaEspera = 'colaborador' | 'miembro' | 'grumete' | 'novato';

const PESO_BASE_ROL_LISTA_ESPERA: Record<RolListaEspera, number> = {
	colaborador: 1,
	grumete: 3,
	miembro: 2,
	novato: 4,
};

/** Cuánto sube el peso dentro de un mismo nivel quien lo intentó y no pudo (D-28): la mitad de la distancia a otro nivel, para no poder saltarlo nunca. */
const AJUSTE_LO_INTENTO = 0.5;

/**
 * El peso de un candidato en la lista de espera (documento 05a §6.4): menor peso, más
 * prioridad. Los cuatro niveles — colaborador (pase libre), miembro, grumete, quien aún no ha
 * vivido ninguna — son la jerarquía de base; `loIntento` (D-22) sube medio punto dentro de su
 * propio nivel (D-28: "sube un escalón dentro del nivel, sin saltarlo"), nunca lo bastante
 * para alcanzar al nivel superior, porque la distancia entre niveles consecutivos es siempre 1.
 */
export const pesoListaEspera = (rol: RolListaEspera, loIntento: boolean): number =>
	PESO_BASE_ROL_LISTA_ESPERA[rol] - (loIntento ? AJUSTE_LO_INTENTO : 0);

export interface CandidatoListaEspera {
	id: string;
	loIntento: boolean;
	rol: RolListaEspera;
}

/**
 * Ordena candidatos de la lista de espera por su peso (`pesoListaEspera`), de mayor a menor
 * prioridad. El orden entre dos candidatos con el mismo peso es el de llegada al array de
 * entrada: `Array.prototype.sort` es estable, así que a igualdad de rol e intento gana quien
 * ya estaba antes en la lista.
 */
export const ordenarListaEspera = (candidatos: readonly CandidatoListaEspera[]): CandidatoListaEspera[] =>
	[...candidatos].sort((a, b) => pesoListaEspera(a.rol, a.loIntento) - pesoListaEspera(b.rol, b.loIntento));

// -----------------------------------------------------------------------------
// costeDeUnirse — el precio de unirse (documento 05a §6.5).
// -----------------------------------------------------------------------------

/** Por qué cuesta lo que cuesta unirse (documento 05a §6.5). */
export type MotivoCosteUnirse =
	| 'primeraGratis'
	| 'cubiertaPorMembresia'
	| 'cuotaPlanGrumete'
	| 'precioPropioDelPlan'
	| 'sinCoste';

export interface ResultadoCosteUnirse {
	cents: number;
	currency: string;
	/** Cierto cuando aceptar la invitación pendiente (`invitadoPor`) haría esta experiencia gratis (documento 05b §10.4). */
	gratisConInvitacion?: boolean;
	invitadoPor?: string;
	motivo: MotivoCosteUnirse;
}

export interface ContextoCosteDeUnirse {
	currency: string;
	/** Si esta es la primera experiencia que vive el grumete (`esNovato`, YA CALCULADO). Irrelevante para cualquier otro rol. */
	esPrimeraDelGrumete: boolean;
	/** El "format" de la experiencia (documento 05a §6.5: la membresía solo cubre el formato "PLAN"). */
	formato: string;
	/** Quien invitó a esta persona a ser miembro, si tiene una invitación pendiente (documento 05b §10.4). Ausente = sin invitación pendiente. */
	invitadoPorMiembroPendiente?: string;
	/** El precio propio de la experiencia, en céntimos. `null` o `0` significa sin precio propio. */
	priceCents: number | null;
	rol: 'colaborador' | 'grumete' | 'miembro';
}

/**
 * Cuánto cuesta unirse (documento 05a §6.5): si la experiencia tiene precio propio, lo paga
 * todo el mundo sin excepción (CA-05a.11) — ni la membresía ni ser colaborador lo cubren. Sin
 * precio propio, la membresía solo cubre el formato "PLAN" (el plan de un día); cualquier otro
 * formato sin precio es gratis para todos. Dentro de un "PLAN" sin precio propio: miembro y
 * colaborador, gratis (cubierto por su condición); el grumete paga `CUOTA_PLAN_GRUMETE_CENTS`
 * a partir de su segunda experiencia vivida — la primera es gratis, porque es el requisito que
 * abre la invitación a miembro, no un descuento (documento 05a §6.5). Si tiene una invitación a
 * miembro pendiente, el coste sigue siendo el mismo: solo se anota que aceptarla lo dejaría a
 * cero, para que la pantalla muestre la comparación del documento 05b §10.4 en vez de un cobro
 * seco.
 */
export const costeDeUnirse = (contexto: ContextoCosteDeUnirse): ResultadoCosteUnirse => {
	if (contexto.priceCents !== null && contexto.priceCents > 0) {
		return { cents: contexto.priceCents, currency: contexto.currency, motivo: 'precioPropioDelPlan' };
	}
	if (contexto.formato !== 'PLAN') {
		return { cents: 0, currency: contexto.currency, motivo: 'sinCoste' };
	}
	if (contexto.rol !== 'grumete') {
		return { cents: 0, currency: contexto.currency, motivo: 'cubiertaPorMembresia' };
	}
	if (contexto.esPrimeraDelGrumete) {
		return { cents: 0, currency: contexto.currency, motivo: 'primeraGratis' };
	}
	const coste: ResultadoCosteUnirse = {
		cents: CUOTA_PLAN_GRUMETE_CENTS,
		currency: contexto.currency,
		motivo: 'cuotaPlanGrumete',
	};
	return contexto.invitadoPorMiembroPendiente === undefined
		? coste
		: { ...coste, gratisConInvitacion: true, invitadoPor: contexto.invitadoPorMiembroPendiente };
};

// -----------------------------------------------------------------------------
// Avisar, cancelar tarde o desaparecer (documento 05a §6.6).
// -----------------------------------------------------------------------------

/**
 * Clasifica una cancelación por su margen respecto al inicio (documento 05a §5.1, D-29; CL-7):
 * `'avisada'` con `MARGEN_AVISO_HORAS` o más de antelación, `'tarde'` con menos, y `'sinFecha'`
 * cuando la experiencia no tiene `startDate` — CL-7 la da por avisada igualmente (sin fecha no
 * hay "tarde" posible), pero se distingue como su propio valor para que quien la use pueda
 * decidir si quiere tratarla igual que `'avisada'` o registrar el caso aparte.
 */
export const clasificarCancelacion = (startDate: Date | null, cancelledAt: Date): 'avisada' | 'tarde' | 'sinFecha' => {
	if (startDate === null) {
		return 'sinFecha';
	}
	const horasDeMargen = (startDate.getTime() - cancelledAt.getTime()) / MS_POR_HORA;
	return horasDeMargen >= MARGEN_AVISO_HORAS ? 'avisada' : 'tarde';
};

/** Los cuatro desenlaces que la tabla del §6.6 distingue: cómo terminó una participación que no llegó a "asistió". */
export type DesenlaceParticipacion = 'rechazado' | 'avisada' | 'tarde' | 'sinFecha' | 'ausencia';

/**
 * Si `desenlace` conserva el sitio en la reserva de novatos (documento 05a §6.6, RN-7): todo
 * conserva menos la ausencia sin avisar — "si avisas, no pierdes tu sitio; si desapareces, sí".
 */
export const conservaPlazaEnReserva = (desenlace: DesenlaceParticipacion): boolean => desenlace !== 'ausencia';

/**
 * Si `desenlace` penaliza la reputación (documento 05a §6.6): cancelar tarde y desaparecer
 * penalizan; ser rechazado ("no fue cosa suya") y avisar a tiempo, no.
 */
export const penalizaReputacion = (desenlace: DesenlaceParticipacion): boolean =>
	desenlace === 'tarde' || desenlace === 'ausencia';

// -----------------------------------------------------------------------------
// Ratio de asistencia, su interpretación y el bloqueo por ausencias (documento 05a §6.6).
// -----------------------------------------------------------------------------

/**
 * Asistencias sobre compromisos, en porcentaje (documento 05a §6.6): un "compromiso" es una
 * participación que llegó a resolverse en `ATTENDED` o `NO_SHOW` — `REQUESTED`, `WAITLISTED`,
 * `CONFIRMED` (todavía sin celebrar), `CANCELLED` y `REJECTED` no cuentan como compromiso
 * resuelto. Sin ningún compromiso todavía, devuelve `PORCENTAJE_COMPLETO`: nadie empieza con
 * un ratio bajo antes de tener historial.
 */
export const ratioAsistencia = (joins: readonly EstadoParticipacion[]): number => {
	const asistidas = joins.filter(status => status === 'ATTENDED').length;
	const compromisos = joins.filter(status => status === 'ATTENDED' || status === 'NO_SHOW').length;
	return compromisos === 0 ? PORCENTAJE_COMPLETO : (asistidas / compromisos) * PORCENTAJE_COMPLETO;
};

/** Los cinco niveles en los que se interpreta el ratio de asistencia (documento 05a §6.6). */
export type NivelAsistencia = 'alta' | 'media' | 'baja' | 'advertenciaFinal' | 'cuentaBloqueada';

/**
 * Interpreta un ratio de asistencia (documento 05a §6.6): por debajo del `UMBRAL_CRITICA` con
 * al menos `MINIMO_COMPROMISOS_CUENTA_BLOQUEADA` compromisos, la cuenta queda bloqueada
 * (requiere hablar con una persona); por debajo del `UMBRAL_BAJA` con al menos
 * `MINIMO_COMPROMISOS_ADVERTENCIA_FINAL`, advertencia final. Sin compromisos suficientes para
 * disparar ninguna de las dos consecuencias, el ratio se lee simplemente en sus tres bandas
 * (alta/media/baja) — un ratio bajo con poco historial no es todavía una señal fiable.
 */
export const interpretarAsistencia = (ratioPct: number, compromisos: number): NivelAsistencia => {
	if (ratioPct < UMBRAL_ASISTENCIA_CRITICA_PCT && compromisos >= MINIMO_COMPROMISOS_CUENTA_BLOQUEADA) {
		return 'cuentaBloqueada';
	}
	if (ratioPct < UMBRAL_ASISTENCIA_BAJA_PCT && compromisos >= MINIMO_COMPROMISOS_ADVERTENCIA_FINAL) {
		return 'advertenciaFinal';
	}
	if (ratioPct >= UMBRAL_ASISTENCIA_ALTA_PCT) {
		return 'alta';
	}
	if (ratioPct >= UMBRAL_ASISTENCIA_MEDIA_PCT) {
		return 'media';
	}
	return 'baja';
};

export interface ResultadoBloqueoPorAusencias {
	bloqueado: boolean;
	/** Hasta cuándo dura el bloqueo. Solo presente cuando `bloqueado` es cierto. */
	hasta?: Date;
}

/**
 * Si esta persona está bloqueada por ausencias (documento 05a §6.6, CL-9: "el servidor lo
 * comprueba, el bloqueo no puede vivir solo en la pantalla"). Con `AUSENCIAS_PARA_BLOQUEO` o
 * más participaciones `NO_SHOW`, el bloqueo dura `DIAS_DE_BLOQUEO` desde la más reciente de
 * ellas — si esa fecha límite ya pasó, el bloqueo se dio por cumplido y no aplica. Decisión
 * cerrada del proyecto: nadie escribe `user.blockedUntil`, se calcula al vuelo cada vez.
 */
export const bloqueadoPorAusencias = (
	joins: readonly ParticipacionParaBloqueo[],
	ahora: Date,
): ResultadoBloqueoPorAusencias => {
	const ausencias = joins.filter(join => join.status === 'NO_SHOW');
	if (ausencias.length < AUSENCIAS_PARA_BLOQUEO) {
		return { bloqueado: false };
	}
	const masReciente = ausencias.reduce((acumulada, actual) =>
		actual.updatedAt.getTime() > acumulada.updatedAt.getTime() ? actual : acumulada,
	);
	const hasta = new Date(masReciente.updatedAt.getTime() + DIAS_DE_BLOQUEO * MS_POR_DIA);
	return hasta.getTime() > ahora.getTime() ? { bloqueado: true, hasta } : { bloqueado: false };
};
